import {
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';

import {
  applePay,
  Client,
  ApplePay as BraintreeApplePay,
} from 'braintree-web';

import { DEFAULT_COUNTRY } from '@/locale';
import { useAppSelector } from '@/store/hooks';
import { logger } from '@/services/logger/logger';
import { ProvidersLibsContext } from '@/services/providersLibs/providersLibsContext';
import { PaymentSelectState } from '@/features/paymentSelect/paymentSelectSlice';

import { deviceSupportsApplePay } from './deviceSupportsApplePay';

const initBraintreeApplePay = async (client: Client): Promise<BraintreeApplePay> => (
  applePay.create({ client })
);

// API reference: https://developer.apple.com/documentation/apple_pay_on_the_web/applepaypaymentrequest
const buildPaymentRequest = async (
  braintreeApplePay: BraintreeApplePay,
  paymentSelectState: PaymentSelectState,
): Promise<ApplePayJS.ApplePayPaymentRequest> => (
  braintreeApplePay.createPaymentRequest({
    countryCode: DEFAULT_COUNTRY,
    currencyCode: paymentSelectState.currency,
    total: {
      amount: paymentSelectState.amount.toString(),
      label: 'Donation',
    },
    requiredBillingContactFields: ['name', 'postalAddress'],
    requiredShippingContactFields: ['email'],
    supportedNetworks: [
      // 'amex', // Disabled while AMEX is not working on our Braintree account
      'electron', // visa sub-brand
      'maestro', // mastercard sub-brand
      'masterCard',
      'visa',
      'vPay', // visa sub-brand
    ],
  }) as ApplePayJS.ApplePayPaymentRequest
);

export function useBraintreeApplepayInit() {
  const paymentSelectState = useAppSelector(state => state.paymentSelect);

  const providersLibs = useContext(ProvidersLibsContext);

  const [braintreeApplePay, setBraintreeApplePay] = useState<BraintreeApplePay>();
  const [paymentRequest, setPaymentRequest] = useState<ApplePayJS.ApplePayPaymentRequest>();

  // Load Braintree braintreeApplePay instance on component load
  useEffect(() => {
    async function loadBraintree() {
      // Wait until Braintree client is available before loading braintreeApplePay
      if (!providersLibs?.braintreeClient) {
        return;
      }

      // No need to load braintreeApplePay if the device doesn't support ApplePay
      if (!deviceSupportsApplePay()) {
        return;
      }

      if (!braintreeApplePay) {
        try {
          const braintreeApplePayInstance = await initBraintreeApplePay(providersLibs.braintreeClient);
          setBraintreeApplePay(braintreeApplePayInstance);
        } catch (error) {
          logger.error('Error initialising Braintree braintreeApplePay');
          logger.error(error);
        }
      }
    }
    void loadBraintree();
  }, [
    providersLibs?.braintreeClient,
    braintreeApplePay,
  ]);

  // Init paymentRequest on component load
  useEffect(() => {
    async function initPaymentRequest() {
      // Wait until Braintree braintreeApplePay is available before initalising paymentRequest
      if (!braintreeApplePay) {
        return;
      }

      // No need to init PaymentRequest if the device doesn't support ApplePay
      if (!deviceSupportsApplePay()) {
        return;
      }

      if (!paymentRequest) {
        try {
          const paymentRequestInstance = await buildPaymentRequest(braintreeApplePay, paymentSelectState);
          setPaymentRequest(paymentRequestInstance);
        } catch (error) {
          logger.error('Error initialising Applepay paymentRequest');
          logger.error(error);
        }
      }
    }
    void initPaymentRequest();
  }, [braintreeApplePay, paymentRequest, paymentSelectState]);

  const beginApplepaySession = useCallback((
    onValidate: (applepaySession: ApplePaySession, validateData: ApplePayJS.ApplePayValidateMerchantEvent) => Promise<void>,
    onPayment: (applepaySession: ApplePaySession, paymentData: ApplePayJS.ApplePayPaymentAuthorizedEvent) => Promise<void>,
    onCancel: (applepaySession: ApplePaySession) => void,
  ) => {
    if (!paymentRequest) {
      logger.error(new Error('Error: paymentRequest not set'));
      return;
    }

    const applepaySession = new ApplePaySession(3, paymentRequest);

    applepaySession.onvalidatemerchant = (validateData: ApplePayJS.ApplePayValidateMerchantEvent) => {
      void onValidate(applepaySession, validateData);
    };
    applepaySession.onpaymentauthorized = (paymentData: ApplePayJS.ApplePayPaymentAuthorizedEvent) => {
      void onPayment(applepaySession, paymentData);
    };
    applepaySession.oncancel = () => {
      onCancel(applepaySession);
    };

    // Trigger Applepay process to begin
    applepaySession.begin();
  }, [paymentRequest]);

  return {
    deviceSupportsApplePay,
    braintreeApplePay,
    applepayReady: (!!paymentRequest && !!braintreeApplePay),
    beginApplepaySession,
  };
}
